/**
 * Crew — recruitable specialists hired at a Space Tavern. Two kinds:
 *   • MDC (Miner Drone Controller) — buffs mining speed + miner capacity.
 *   • FTP (Fighter Pilot) — assigned to an escort fighter; buffs its fire rate,
 *     HP and shield. A fighter with no pilot cannot launch.
 *
 * Like shield generators, each recruit rolls a rarity (Common → Legendary). Its
 * buff also grows with the crew member's TIER, which you raise in the Tavern's
 * Upgrade ribbon. Portraits come from MDC_Sheet / FTP_Sheet (backgrounds kept).
 */
import { rollRarity, type Rarity } from "@/game/mods/modifications";

export type CrewType = "mdc" | "ftp" | "rdc" | "gc" | "fdc";
export type Gender = "m" | "f";

export interface CrewMember {
  id: string;
  type: CrewType;
  rarity: Rarity;
  tier: number; // 1..CREW_MAX_TIER
  name: string;
  gender: Gender;
  portrait: string; // portraits/<portrait>.png (background kept)
  kia?: boolean; // Fighter Pilot Killed In Action — grounded until rerolled at a tavern
}

export const CREW_MAX_TIER = 5;
export const RECRUIT_COST = 180; // Units to recruit one crew member
export const REROLL_COST = 120; // Units to re-roll a crew member's rarity/portrait/name
/** Units to raise a crew member from `tier` → `tier+1`. */
export function upgradeCost(tier: number): number {
  return 250 * tier;
}

// Portrait frames sliced from the sheets, tagged with apparent gender so names
// match. MDC_Sheet is a 3×2 grid (6); FTP_Sheet is a single portrait.
const MDC_PORTRAITS: { file: string; gender: Gender }[] = [
  { file: "mdc_0", gender: "m" },
  { file: "mdc_1", gender: "f" },
  { file: "mdc_2", gender: "m" },
  { file: "mdc_3", gender: "f" },
  { file: "mdc_4", gender: "m" },
  { file: "mdc_5", gender: "m" },
];
const FTP_PORTRAITS: { file: string; gender: Gender }[] = [
  { file: "ftp_0", gender: "m" }, // VULTURE-1
  { file: "ftp_1", gender: "f" }, // RAVEN-2
  { file: "ftp_2", gender: "m" }, // VIPER-3
];

const MALE_FIRST = [
  "James", "Michael", "Robert", "David", "John", "Chris", "Daniel", "Anthony",
  "Mark", "Ryan", "Kevin", "Brian", "Jason", "Eric", "Tyler", "Aaron", "Adam",
  "Nathan", "Jose", "Marcus",
];
const FEMALE_FIRST = [
  "Mary", "Jennifer", "Linda", "Patricia", "Elizabeth", "Susan", "Jessica",
  "Sarah", "Karen", "Lisa", "Emily", "Ashley", "Amanda", "Megan", "Rachel",
  "Laura", "Nicole", "Hannah", "Olivia", "Diana",
];
const LAST = [
  "Smith", "Johnson", "Williams", "Brown", "Jones", "Garcia", "Miller", "Davis",
  "Rodriguez", "Martinez", "Lopez", "Wilson", "Anderson", "Taylor", "Moore",
  "Jackson", "Martin", "Lee", "Walker", "Hall", "Young", "King", "Wright",
  "Scott", "Green", "Baker", "Adams", "Nelson", "Carter", "Mitchell",
];

let crewCounter = 0;
function pick<T>(arr: T[], rng: () => number): T {
  return arr[Math.floor(rng() * arr.length)]!;
}

/** Recruit a fresh crew member of the given type (rarity-weighted like mods). */
export function makeCrew(type: CrewType, rng: () => number = Math.random): CrewMember {
  const rarity = rollRarity(rng);
  const port = pick(
    type === "ftp" || type === "fdc" ? FTP_PORTRAITS : MDC_PORTRAITS,
    rng,
  ); // rdc + gc share MDC art; fdc shares FTP art
  const first = pick(port.gender === "m" ? MALE_FIRST : FEMALE_FIRST, rng);
  const last = pick(LAST, rng);
  crewCounter += 1;
  return {
    id: `crew_${Date.now().toString(36)}_${crewCounter}`,
    type,
    rarity,
    tier: 1,
    name: `${first} ${last}`,
    gender: port.gender,
    portrait: port.file,
  };
}

/** Re-roll an existing crew member's rarity, portrait and name (same type; tier
 * resets to 1). Keeps the same id so fighter pilot assignments survive. Also
 * revives a Killed-In-Action pilot — this is how the player replaces a lost pilot. */
export function rerollCrew(m: CrewMember, rng: () => number = Math.random): void {
  m.rarity = rollRarity(rng);
  const port = pick(
    m.type === "ftp" || m.type === "fdc" ? FTP_PORTRAITS : MDC_PORTRAITS,
    rng,
  ); // rdc + gc share MDC art; fdc shares FTP art
  const first = pick(port.gender === "m" ? MALE_FIRST : FEMALE_FIRST, rng);
  m.portrait = port.file;
  m.gender = port.gender;
  m.name = `${first} ${pick(LAST, rng)}`;
  m.tier = 1;
  m.kia = false;
}

export interface MdcBuff {
  mineSpeedMul: number; // hull + drone mining rate
  cargoMul: number; // drone mineral capacity
}
export interface FtpBuff {
  fireMul: number; // fighter fire rate
  hpMul: number;
  shieldMul: number;
}

const MDC_BASE: Record<Rarity, { mine: number; cargo: number }> = {
  common: { mine: 0.1, cargo: 0.1 },
  rare: { mine: 0.2, cargo: 0.2 },
  epic: { mine: 0.35, cargo: 0.3 },
  legendary: { mine: 0.55, cargo: 0.45 },
};
const FTP_BASE: Record<Rarity, { fire: number; hp: number; shield: number }> = {
  common: { fire: 0.1, hp: 0.1, shield: 0.1 },
  rare: { fire: 0.2, hp: 0.18, shield: 0.18 },
  epic: { fire: 0.35, hp: 0.3, shield: 0.3 },
  legendary: { fire: 0.55, hp: 0.5, shield: 0.5 },
};

/** All crew buffs are scaled to 30% of their raw value (−70% nerf). */
export const CREW_BUFF_SCALE = 0.3;

export function mdcBuff(m: CrewMember): MdcBuff {
  const b = MDC_BASE[m.rarity];
  const t = m.tier - 1;
  return {
    mineSpeedMul: 1 + (b.mine + t * 0.08) * CREW_BUFF_SCALE,
    cargoMul: 1 + (b.cargo + t * 0.06) * CREW_BUFF_SCALE,
  };
}
export function ftpBuff(m: CrewMember): FtpBuff {
  const b = FTP_BASE[m.rarity];
  const t = m.tier - 1;
  return {
    fireMul: 1 + (b.fire + t * 0.08) * CREW_BUFF_SCALE,
    hpMul: 1 + (b.hp + t * 0.07) * CREW_BUFF_SCALE,
    shieldMul: 1 + (b.shield + t * 0.07) * CREW_BUFF_SCALE,
  };
}

export interface RdcBuff {
  repairMul: number; // repair-drone healing rate
}
const RDC_BASE: Record<Rarity, { repair: number }> = {
  common: { repair: 0.15 },
  rare: { repair: 0.3 },
  epic: { repair: 0.5 },
  legendary: { repair: 0.8 },
};
export function rdcBuff(m: CrewMember): RdcBuff {
  const b = RDC_BASE[m.rarity];
  const t = m.tier - 1;
  return { repairMul: 1 + (b.repair + t * 0.1) * CREW_BUFF_SCALE };
}

// Gun Commander (Battleship MK V only) — buffs turrets + cannons: damage, turn rate,
// and a little fire rate.
export interface GcBuff {
  damageMul: number;
  turnRateMul: number;
  fireMul: number;
}
const GC_BASE: Record<Rarity, { dmg: number; turn: number; fire: number }> = {
  common: { dmg: 0.15, turn: 0.12, fire: 0.05 },
  rare: { dmg: 0.3, turn: 0.22, fire: 0.1 },
  epic: { dmg: 0.5, turn: 0.36, fire: 0.15 },
  legendary: { dmg: 0.8, turn: 0.55, fire: 0.22 },
};
export function gcBuff(m: CrewMember): GcBuff {
  const b = GC_BASE[m.rarity];
  const t = m.tier - 1;
  return {
    damageMul: 1 + (b.dmg + t * 0.1) * CREW_BUFF_SCALE,
    turnRateMul: 1 + (b.turn + t * 0.08) * CREW_BUFF_SCALE,
    fireMul: 1 + (b.fire + t * 0.04) * CREW_BUFF_SCALE,
  };
}

// Fighter Drone Controller (Swarm-lord MK V only) — buffs the combat fighter drones:
// damage, speed, HP.
export interface FdcBuff {
  damageMul: number;
  speedMul: number;
  hpMul: number;
}
const FDC_BASE: Record<Rarity, { dmg: number; speed: number; hp: number }> = {
  common: { dmg: 0.12, speed: 0.1, hp: 0.1 },
  rare: { dmg: 0.24, speed: 0.18, hp: 0.18 },
  epic: { dmg: 0.4, speed: 0.3, hp: 0.3 },
  legendary: { dmg: 0.65, speed: 0.45, hp: 0.5 },
};
export function fdcBuff(m: CrewMember): FdcBuff {
  const b = FDC_BASE[m.rarity];
  const t = m.tier - 1;
  return {
    damageMul: 1 + (b.dmg + t * 0.09) * CREW_BUFF_SCALE,
    speedMul: 1 + (b.speed + t * 0.07) * CREW_BUFF_SCALE,
    hpMul: 1 + (b.hp + t * 0.07) * CREW_BUFF_SCALE,
  };
}

export function crewTitle(m: CrewMember): string {
  return m.type === "mdc"
    ? "Miner Drone Controller"
    : m.type === "rdc"
      ? "Repair Drone Controller"
      : m.type === "gc"
        ? "Gun Commander"
        : m.type === "fdc"
          ? "Fighter Drone Controller"
          : "Fighter Pilot";
}
